/**
 * Generic frontend logging via BetterStack (Logtail browser SDK).
 *
 * Ships structured events straight from the browser to a BetterStack source.
 * Backend logs already reach BetterStack via DigitalOcean log forwarding; these
 * frontend events land in the same account. Every event is auto-tagged with the
 * `app` surface (`staff` | `portal`) so the two can be filtered apart while
 * sharing one source.
 *
 * Configured at build time through Vite env vars:
 *   - VITE_BETTERSTACK_SOURCE_TOKEN   (BetterStack source token)
 *   - VITE_BETTERSTACK_INGESTING_HOST (e.g. sNNNN.eu-nbg-2.betterstackdata.com)
 *
 * When the token/host are absent (local dev), calls fall back to console.*
 * instead of shipping to BetterStack, so callers never need to guard and
 * errors are still visible while developing.
 */

import { Logtail } from '@logtail/browser';

const sourceToken = import.meta.env.VITE_BETTERSTACK_SOURCE_TOKEN;
const ingestingHost = import.meta.env.VITE_BETTERSTACK_INGESTING_HOST;

const logtail =
  sourceToken && ingestingHost
    ? new Logtail(sourceToken, { endpoint: `https://${ingestingHost}` })
    : null;

export type LogContext = Record<string, unknown>;

type LogLevel = 'info' | 'warn' | 'error';

/** Which app surface emitted the event, derived from the current route. */
function currentApp(): 'portal' | 'staff' {
  return typeof window !== 'undefined' &&
    window.location.pathname.startsWith('/portal')
    ? 'portal'
    : 'staff';
}

/** JWT claims we lift into logs to answer "which user/session hit this?". */
type IdentityClaims = {
  sub?: string;
  session_id?: string;
  investor_id?: string;
  is_impersonation?: boolean;
  admin_user_id?: string;
};

/**
 * Best-effort identity of the current session, read from whichever auth token
 * is in localStorage (portal or staff). Decodes the JWT payload only - no
 * signature check; the backend already validates that. Every field is optional
 * and the whole thing is wrapped in try/catch so logging can never throw.
 *
 * `session_id` matches the backend session, so a frontend error and the
 * backend request logs for the same user line up in BetterStack.
 */
function currentIdentity(): LogContext {
  if (typeof window === 'undefined') return {};
  try {
    const token =
      localStorage.getItem('investor_portal_token') ??
      localStorage.getItem('access_token');
    if (!token) return {};
    const [, payload] = token.split('.');
    if (!payload) return {};
    const claims = JSON.parse(
      atob(payload.replaceAll('-', '+').replaceAll('_', '/'))
    ) as IdentityClaims;
    return {
      user_id: claims.sub,
      session_id: claims.session_id,
      investor_id: claims.investor_id,
      is_impersonation: claims.is_impersonation,
      admin_user_id: claims.admin_user_id,
    };
  } catch {
    return {};
  }
}

function writeLog(
  level: LogLevel,
  message: string,
  context?: LogContext
): void {
  if (!logtail) {
    // Not configured (local dev): fall back to the console so errors are
    // still visible while developing.
    if (context) {
      console[level](message, context);
    } else {
      console[level](message);
    }
    return;
  }
  logtail[level](message, {
    app: currentApp(),
    ...currentIdentity(),
    ...context,
  });
}

/**
 * Structured app-wide logger. Ships to BetterStack when configured (prod);
 * falls back to console.* otherwise (dev), so callers only need one call.
 */
export const log = {
  info: (message: string, context?: LogContext) =>
    writeLog('info', message, context),
  warn: (message: string, context?: LogContext) =>
    writeLog('warn', message, context),
  error: (message: string, context?: LogContext) =>
    writeLog('error', message, context),
};

/** Extract a safe, human-readable message from an unknown thrown value. */
export function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}
